import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { SearchInput } from '@/components/SearchInput';
import { Pagination } from '@/components/Pagination';
import { StatusPill } from '@/components/StatusPill';
import { usersService, UserRow } from '@/services/users.service';
import { Building2, Mail, ShieldCheck, UserRound } from 'lucide-react';
import clsx from 'clsx';

type RoleFilter = 'all' | 'company_admin' | 'company_user';

const ROLE: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
  company_admin: { label: 'Admin', cls: 'bg-primary/15 text-primary', icon: <ShieldCheck size={11} /> },
  company_user:  { label: 'Member', cls: 'bg-surface-alt text-text-muted', icon: <UserRound size={11} /> },
};

const initials = (u: UserRow) =>
  `${u.firstName?.[0] ?? ''}${u.lastName?.[0] ?? ''}`.toUpperCase() || (u.email?.[0] ?? '?').toUpperCase();

/**
 * Every person across every client company. One row per user — identity on the
 * left, where they belong in the middle, what they can do on the right.
 */
export default function AllUsers() {
  const [data, setData] = useState<{ items: UserRow[]; total: number; page: number; limit: number } | null>(null);
  const [q, setQ] = useState('');
  const [role, setRole] = useState<RoleFilter>('all');
  const [page, setPage] = useState(1);

  useEffect(() => { usersService.list({ q: q || undefined, page, limit: 30 }).then(setData); }, [q, page]);

  // Role narrowing is client-side over the current page — the list endpoint
  // takes no role param, and pretending otherwise would filter silently wrong.
  const rows = useMemo(
    () => (data?.items ?? []).filter((u) => role === 'all' || u.role === role),
    [data, role],
  );

  const tabs: { key: RoleFilter; label: string }[] = [
    { key: 'all', label: 'Everyone' },
    { key: 'company_admin', label: 'Admins' },
    { key: 'company_user', label: 'Members' },
  ];

  return (
    <>
      <Topbar
        title="Users"
        breadcrumb="Customers / Users"
        right={data ? <span className="text-xs text-text-muted">{data.total} across all clients</span> : undefined}
      />
      <div className="space-y-3 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-1 rounded-lg bg-surface-alt p-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setRole(t.key)}
                className={clsx(
                  'rounded-md px-3 py-1.5 text-[13px] transition-colors',
                  role === t.key ? 'bg-surface font-semibold text-primary shadow-sm' : 'text-text-muted hover:text-text-primary',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          <SearchInput value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search name or email…" />
        </div>

        {!data ? (
          <div className="space-y-2">
            {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-surface-alt/60" />)}
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-10 text-center">
            <p className="text-sm font-medium text-text-primary">No users match</p>
            <p className="mt-1 text-xs text-text-muted">
              {q ? `Nothing for “${q}”. Try an email or a company name.` : 'Change the filter to see more people.'}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border bg-surface">
            {rows.map((u, i) => {
              const r = ROLE[u.role] ?? { label: u.role, cls: 'bg-surface-alt text-text-muted', icon: <UserRound size={11} /> };
              return (
                <div
                  key={u._id}
                  className={clsx(
                    'flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 transition-colors hover:bg-bg/40',
                    i > 0 && 'border-t border-border',
                  )}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[12px] font-bold text-primary">
                    {initials(u)}
                  </span>
                  <div className="min-w-[180px] flex-1">
                    <div className="truncate text-[13.5px] font-medium text-text-primary">
                      {u.firstName} {u.lastName}
                    </div>
                    <a
                      href={`mailto:${u.email}`}
                      className="inline-flex items-center gap-1 truncate text-[11.5px] text-text-muted hover:text-primary"
                    >
                      <Mail size={11} /> {u.email}
                    </a>
                  </div>
                  <Link
                    to={`/companies/${u.companyUid}`}
                    className="inline-flex min-w-0 items-center gap-1.5 text-[12.5px] text-text-secondary hover:text-primary"
                  >
                    <Building2 size={12} className="shrink-0 text-text-muted" />
                    <span className="truncate">{u.company?.name ?? u.companyUid.slice(-6)}</span>
                  </Link>
                  <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${r.cls}`}>
                    {r.icon}{r.label}
                  </span>
                  <span className="shrink-0"><StatusPill status={u.status} /></span>
                  <span className="shrink-0 text-[11.5px] tabular-nums text-text-muted">
                    Joined {new Date(u.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        {data && <Pagination page={data.page} total={data.total} limit={data.limit} onChange={setPage} />}
      </div>
    </>
  );
}
